import { inject, injectable } from 'inversify'
import { action, makeObservable, observable, runInAction } from 'mobx'
import type { UserScopedState } from '@core/interfaces/user-scoped-state'
import { FiltersConfig, type FilterScope } from './filters.config'

/** What the user has narrowed a list to. Ids and strings only — no domain meaning. */
export type ActiveFiltersPm = {
  /** Per filter id: the text typed, the choice id picked, or 'on' for a toggle. */
  readonly values: Readonly<Record<string, string>>
  readonly sortId: string
  readonly ascending: boolean
  /** 1-based. Narrowing or re-sorting sends it back to 1 — see `write`. */
  readonly page: number
}

/**
 * Which filters are active, per list. One Repository keyed by scope rather than
 * one per list, and no base class either: the part that is identical everywhere
 * is this selection state, and the part that differs — which items a selection
 * keeps — is domain knowledge that belongs in each list's own Presenter. A base
 * class would invert that, pushing the domain down into subclasses of a state
 * container.
 *
 * It lives in a Repository rather than on a Presenter because of the lifetime
 * (P-28): the toolbar that sets a filter unmounts when the user leaves the page,
 * and the filter is expected to still be there when they come back. That makes
 * it user-scoped state, which is why it registers IUserScopedState — the
 * mandatory second step P-28 warns is easy to forget, and without it one user's
 * filters would greet the next login.
 */
@injectable()
export class FiltersRepository implements UserScopedState {
  private readonly filtersConfig: FiltersConfig

  /** Scope → selection. Absent until that list is first narrowed. */
  activeByScopePm: Readonly<Partial<Record<FilterScope, ActiveFiltersPm>>> = {}

  constructor(@inject(FiltersConfig) filtersConfig: FiltersConfig) {
    this.filtersConfig = filtersConfig

    makeObservable(this, {
      activeByScopePm: observable.ref,
      setValue: action,
      clearValue: action,
      toggleValue: action,
      setSort: action,
      setSortId: action,
      toggleSortDirection: action,
      cycleSort: action,
      restore: action,
      setPage: action,
      clearScope: action,
    })
  }

  /** Never null: an un-narrowed list reads as "nothing selected, default sort". */
  activeFor = (scope: FilterScope): ActiveFiltersPm => {
    const stored = this.activeByScopePm[scope]
    if (stored) return stored

    const definition = this.filtersConfig.definitionFor(scope)
    return {
      values: {},
      sortId: definition.defaultSortId ?? '',
      ascending: definition.defaultAscending ?? true,
      page: 1,
    }
  }

  /** True when anything is narrowed — what a "Clear" control keys off. */
  hasActiveFilters = (scope: FilterScope): boolean =>
    Object.values(this.activeFor(scope).values).some((value) => value.length > 0)

  setValue = (scope: FilterScope, filterId: string, value: string): void => {
    // Empty means "not selected" rather than "selected as empty", so clearing a
    // text box and never touching it are the same state.
    if (value.length === 0) {
      this.clearValue(scope, filterId)
      return
    }
    const current = this.activeFor(scope)
    this.write(scope, { ...current, values: { ...current.values, [filterId]: value } })
  }

  clearValue = (scope: FilterScope, filterId: string): void => {
    const current = this.activeFor(scope)
    const values = { ...current.values }
    delete values[filterId]
    this.write(scope, { ...current, values })
  }

  /**
   * On becomes off and off becomes on. Here rather than in each list's Presenter
   * because 'on' is this store's own encoding of a toggle, not domain knowledge —
   * six Presenters re-deriving it is six chances to encode it differently.
   */
  toggleValue = (scope: FilterScope, filterId: string): void => {
    if (this.activeFor(scope).values[filterId] === 'on') {
      this.clearValue(scope, filterId)
      return
    }
    this.setValue(scope, filterId, 'on')
  }

  setSort = (scope: FilterScope, sortId: string, ascending: boolean): void => {
    this.write(scope, { ...this.activeFor(scope), sortId, ascending })
  }

  setPage = (scope: FilterScope, page: number): void => {
    this.write(scope, { ...this.activeFor(scope), page: Math.max(1, page) }, { keepPage: true })
  }

  /** Change the sort key, keeping the direction the user already chose. */
  setSortId = (scope: FilterScope, sortId: string): void => {
    this.setSort(scope, sortId, this.activeFor(scope).ascending)
  }

  toggleSortDirection = (scope: FilterScope): void => {
    const active = this.activeFor(scope)
    this.setSort(scope, active.sortId, !active.ascending)
  }

  /**
   * One column header, clicked repeatedly: sort by it, reverse it, then back to
   * the list's own default.
   *
   * The third step is deliberately *not* "unsorted". A list has no unsorted
   * state — the rows still arrive in some order, and a control that claimed to
   * remove ordering would be describing something the user cannot see. Restoring
   * the declared default is the same gesture with an honest name.
   *
   * On the default column that collapses to a two-step cycle, because step three
   * and step one are the same state. No special case is needed for it, and
   * pretending otherwise would mean a click that appears to do nothing.
   */
  cycleSort = (scope: FilterScope, sortId: string): void => {
    const active = this.activeFor(scope)
    const definition = this.filtersConfig.definitionFor(scope)
    const start = (definition.sorts ?? []).find((sort) => sort.id === sortId)?.startAscending ?? true

    if (active.sortId !== sortId) {
      this.setSort(scope, sortId, start)
      return
    }
    if (active.ascending === start) {
      this.setSort(scope, sortId, !start)
      return
    }
    this.setSort(scope, definition.defaultSortId ?? '', definition.defaultAscending ?? true)
  }

  /**
   * Adopt a whole selection at once — what restoring from a URL needs. Separate
   * from the setters because it is one atomic publication rather than a
   * sequence: replaying it field by field would make every intermediate state
   * observable, and each one would be written back to the URL.
   */
  restore = (scope: FilterScope, active: ActiveFiltersPm): void => {
    this.write(scope, active)
  }

  clearScope = (scope: FilterScope): void => {
    const rest = { ...this.activeByScopePm }
    delete rest[scope]
    this.activeByScopePm = rest
  }

  /**
   * Every change resets to page 1 unless it *is* a page change.
   *
   * Here rather than in each caller because the failure it prevents is silent:
   * narrowing a list while on page 4 leaves the reader looking at an empty page
   * of a three-page result, with no clue that anything matched.
   */
  private write(scope: FilterScope, active: ActiveFiltersPm, options: { keepPage?: boolean } = {}): void {
    const next = options.keepPage === true ? active : { ...active, page: 1 }
    this.activeByScopePm = { ...this.activeByScopePm, [scope]: next }
  }

  resetUserScopedState = (): void => {
    runInAction(() => {
      this.activeByScopePm = {}
    })
  }
}
